import { useEffect, useRef, useState } from "react";
import {
  BarChart3,
  Boxes,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  GraduationCap,
  Grip,
  Leaf,
  LockKeyhole,
  MessageSquareText,
  PackageCheck,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  Store,
  UserRound,
  Users,
  X,
} from "lucide-react";
import {
  askPrivateLocalAssistant,
  isPrivateLocalAssistantEnabled,
} from "../lib/localAssistant";

const MODES = [
  {
    id: "owner",
    label: "Owner Console",
    shortLabel: "Katie",
    icon: BriefcaseBusiness,
    eyebrow: "Private owner workspace",
    title: "Your enterprise, clearly in view.",
    description:
      "Governance, approvals, botanical intelligence, customer continuity, finance, learning, and operations—organized around decisions rather than dashboards for their own sake.",
    stats: [
      ["Approvals", "4", "2 require owner review"],
      ["Customers", "128", "11 active plant cases"],
      ["Inventory", "342", "18 low-stock signals"],
      ["Runway", "14 mo", "Preview scenario"],
    ],
    sections: [
      ["Governance & approvals", "Review consequential actions, evidence, policy, and rollback before adoption.", ShieldCheck],
      ["CRM & continuity", "Carry customer, plant, order, and follow-up context across every authorized interaction.", Users],
      ["Finance intelligence", "See cash flow, margin, forecasting, bookkeeping review, and owner-private analysis.", CircleDollarSign],
      ["Botanical intelligence", "Research identity, care, pathology, cultivation, and source-backed recommendations.", Leaf],
      ["Learning & LMS", "Manage governed improvement, training, evaluation, and accountable knowledge growth.", GraduationCap],
      ["Enterprise settings", "Control permissions, autonomy, providers, audit, records, and capability status.", Settings2],
    ],
  },
  {
    id: "seller",
    label: "Plant Seller Portal",
    shortLabel: "Seller",
    icon: Store,
    eyebrow: "Independent seller workspace",
    title: "A clear path from onboarding to fulfilled order.",
    description:
      "A focused workspace for inventory, orders, compliance, plant records, and conversations—without exposing private owner or customer systems.",
    stats: [
      ["Onboarding", "82%", "2 steps remaining"],
      ["Live listings", "24", "3 drafts"],
      ["Open orders", "7", "2 ship today"],
      ["Compliance", "Ready", "Next review Aug 14"],
    ],
    sections: [
      ["Seller onboarding", "Track identity, agreements, permissions, operating details, and readiness checks.", ClipboardCheck],
      ["Inventory", "Manage plants, quantities, condition, pricing, photos, and availability.", Boxes],
      ["Orders", "See preparation, packaging, shipping, pickup, and customer handoff status.", PackageCheck],
      ["Compliance", "Keep jurisdiction, source, license, review, and expiration evidence visible.", ShieldCheck],
      ["Plant intelligence", "Use scoped botanical support for listings, care guidance, and customer questions.", Leaf],
      ["Messaging", "Keep seller, Grace, and order conversations attached to the right records.", MessageSquareText],
    ],
  },
  {
    id: "enterprise",
    label: "Enterprise Customer Demo",
    shortLabel: "Enterprise",
    icon: Building2,
    eyebrow: "Prospective customer experience",
    title: "Agentic intelligence with enterprise boundaries.",
    description:
      "A polished demonstration of how one governed intelligence layer can understand mixed requests, coordinate authorized capabilities, and return one accountable answer. Each customer workspace is configured from the software package they purchase and the user's authorized role.",
    stats: [
      ["Workspace", "Configured", "From purchased package"],
      ["Role access", "Scoped", "Owner, manager, employee"],
      ["Governed actions", "100%", "Permission checked"],
      ["Audit coverage", "Full", "Typed outcomes"],
    ],
    sections: [
      ["Multi-capability reasoning", "Coordinate research, records, workflows, and decisions in one coherent plan.", Sparkles],
      ["Governed execution", "Separate reasoning from consequential action with permissions and approvals.", ShieldCheck],
      ["Enterprise knowledge", "Route reference knowledge, operational records, and private intelligence correctly.", Building2],
      ["Operational intelligence", "Connect CRM, inventory, finance, HR, LMS, and workflows through bounded tools.", BarChart3],
      ["Auditable outcomes", "Preserve evidence, results, changed records, failures, and rollback paths.", CheckCircle2],
      ["Configurable platform", "Adapt capabilities, autonomy, providers, terminology, and access to each organization.", Settings2],
    ],
  },
];

const SIGN_IN_TYPES = [
  {
    id: "owner",
    label: "Owner Console",
    description: "Private governance, business intelligence, approvals, and enterprise controls.",
    icon: BriefcaseBusiness,
    email: "katie@graceunderumbrella.com",
  },
  {
    id: "seller",
    label: "Plant Seller Portal",
    description: "Seller onboarding, inventory, orders, compliance, and messaging.",
    icon: Store,
    email: "seller@graceunderumbrella.com",
  },
  {
    id: "enterprise",
    label: "Enterprise Customer",
    description: "A private product demonstration for prospective organizations.",
    icon: Building2,
    email: "team@company.com",
  },
];

function initialPosition() {
  return {
    x: Math.max(18, window.innerWidth - 92),
    y: Math.max(18, window.innerHeight - 102),
  };
}

function PreviewBadge() {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-white/75">
      <LockKeyhole size={12} /> Interface preview · no live connection
    </span>
  );
}

function ReservedBrand({ compact = false }) {
  return (
    <span className="inline-flex items-center gap-2" aria-label="AI assistant — public name reserved pending trademark">
      <span
        aria-hidden="true"
        className={`relative inline-block overflow-hidden rounded-md border border-[#bfe8e1]/25 bg-[#bfe8e1]/10 ${compact ? "h-5 w-28" : "h-7 w-36"}`}
      >
        <span className="absolute inset-x-3 top-1/2 h-2 -translate-y-1/2 rounded-full bg-[#d5f1eb]/75 blur-[5px]" />
      </span>
      {!compact && (
        <span className="text-[8px] font-medium uppercase tracking-[0.18em] text-[#bfe8e1]/55">
          Public name reserved
        </span>
      )}
    </span>
  );
}

function DragonflyMark({ size = 24, className = "" }) {
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.35"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="24" cy="9" r="2.4" />
      <path d="M24 12v25" />
      <path d="M21.8 16.5c-6.8-7.2-14.8-7.4-16.4-3.7-1.8 4.1 6.8 8.7 16.4 8.1" />
      <path d="M26.2 16.5c6.8-7.2 14.8-7.4 16.4-3.7 1.8 4.1-6.8 8.7-16.4 8.1" />
      <path d="M21.8 23.4c-7.8.2-13.8 4.6-12.3 8.1 1.5 3.4 8.6.8 13.1-4.9" />
      <path d="M26.2 23.4c7.8.2 13.8 4.6 12.3 8.1-1.5 3.4-8.6.8-13.1-4.9" />
      <path d="M20.8 15.2h6.4M20.5 21.2h7M21.2 27.2h5.6M22 32.7h4" />
      <path d="M24 37l-2.2 4.2M24 37l2.2 4.2" />
    </svg>
  );
}

export function GraceAssistant() {
  const [position, setPosition] = useState(initialPosition);
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatQuestion, setChatQuestion] = useState("");
  const [chatNotice, setChatNotice] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [entered, setEntered] = useState(false);
  const [entryStep, setEntryStep] = useState("choose");
  const [signInType, setSignInType] = useState("owner");
  const [enterpriseRole, setEnterpriseRole] = useState("owner");
  const [guestMode, setGuestMode] = useState(false);
  const [modeId, setModeId] = useState("owner");
  const [activeModule, setActiveModule] = useState(null);
  const dragRef = useRef(null);
  const closeRef = useRef(null);
  const activeMode = MODES.find((mode) => mode.id === modeId) || MODES[0];
  const activeSignIn = SIGN_IN_TYPES.find((type) => type.id === signInType) || SIGN_IN_TYPES[0];
  const ActiveSignInIcon = activeSignIn.icon;
  const ActiveModuleIcon = activeModule?.[2] || Sparkles;

  useEffect(() => {
    const clamp = () =>
      setPosition((current) => ({
        x: Math.min(Math.max(14, current.x), Math.max(14, window.innerWidth - 74)),
        y: Math.min(Math.max(14, current.y), Math.max(14, window.innerHeight - 74)),
      }));
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const startDrag = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      moved: false,
    };
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dx) + Math.abs(dy) > 5) drag.moved = true;
    setPosition({
      x: Math.min(Math.max(14, drag.originX + dx), Math.max(14, window.innerWidth - 74)),
      y: Math.min(Math.max(14, drag.originY + dy), Math.max(14, window.innerHeight - 74)),
    });
  };

  const finishDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (!drag.moved) setChatOpen((current) => !current);
  };

  const openAccount = () => {
    setChatOpen(false);
    setEntered(false);
    setGuestMode(false);
    setActiveModule(null);
    setEntryStep("choose");
    setOpen(true);
  };

  const chooseSignIn = (id) => {
    setSignInType(id);
    setModeId(id);
    setActiveModule(null);
    setEntryStep("signin");
  };

  const enterSignedPreview = () => {
    setGuestMode(false);
    setModeId(signInType);
    setActiveModule(null);
    setEntered(true);
  };

  const enterGuestPreview = () => {
    setGuestMode(true);
    setModeId("enterprise");
    setActiveModule(null);
    setEntered(true);
  };

  const openGraceCustomerSignIn = () => {
    setOpen(false);
    window.requestAnimationFrame(() => {
      const siteSignIn = document.querySelector('[data-testid="signin-btn"]');
      if (siteSignIn instanceof HTMLElement) siteSignIn.click();
    });
  };

  const submitChatQuestion = async (event) => {
    event.preventDefault();
    const question = chatQuestion.trim();
    setChatQuestion("");
    if (!question || chatBusy) return;
    if (!isPrivateLocalAssistantEnabled()) {
      setChatNotice("This public preview is intentionally disconnected from private systems.");
      return;
    }

    setChatBusy(true);
    setChatNotice("Thinking through the governed local runtime…");
    try {
      const response = await askPrivateLocalAssistant(question);
      setChatNotice(response.answer);
    } catch {
      setChatNotice("The private local assistant is not running yet. Start the local bridge and try again.");
    } finally {
      setChatBusy(false);
    }
  };

  return (
    <>
      <div
        style={{ left: position.x, top: position.y }}
        className="fixed z-[65]"
        data-testid="assistant-avatar-group"
      >
        {chatOpen && (
          <div className="absolute bottom-[76px] right-0 w-[min(330px,calc(100vw-28px))] overflow-hidden rounded-[24px] border border-[#9fcfc8] bg-[#f7f2e8] text-[#071a14] shadow-[0_24px_70px_rgba(4,18,14,0.28)]">
            <div className="flex items-center justify-between bg-[#071a14] px-5 py-4 text-[#d5f1eb]">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-[#bfe8e1] text-[#071a14]">
                  <DragonflyMark size={20} />
                </span>
                <div>
                  <p className="font-display text-lg leading-none">Ask the assistant</p>
                  <p className="mt-1 text-[9px] uppercase tracking-[0.18em] text-[#bfe8e1]/65">
                    {isPrivateLocalAssistantEnabled() ? "Private local connection" : "Chat preview"}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setChatOpen(false)} aria-label="Close assistant chat" className="rounded-full p-2 hover:bg-white/10">
                <X size={16} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm leading-6 text-[#496158]">What would you like help with?</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Help my plant", "Find a product", "Seller questions"].map((question) => (
                  <button
                    key={question}
                    type="button"
                    onClick={() => { setChatQuestion(question); setChatNotice(""); }}
                    className="rounded-full border border-[#a8cec7] bg-[#dff3ef] px-3 py-2 text-xs text-[#173d34] transition hover:bg-[#ccebe5]"
                  >
                    {question}
                  </button>
                ))}
              </div>
              <form onSubmit={submitChatQuestion} className="mt-4 flex items-center gap-2 rounded-2xl border border-[#cfc8ba] bg-white px-3 py-2">
                <input
                  value={chatQuestion}
                  onChange={(event) => { setChatQuestion(event.target.value); setChatNotice(""); }}
                  placeholder="Type a question..."
                  aria-label="Question for the assistant"
                  className="min-w-0 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-[#819188]"
                />
                <button type="submit" disabled={chatBusy} aria-label="Ask question" className="grid h-9 w-9 place-items-center rounded-full bg-[#071a14] text-[#d5f1eb] disabled:cursor-wait disabled:opacity-55">
                  <Send size={15} />
                </button>
              </form>
              {chatNotice && <p className="mt-3 text-[11px] leading-5 text-[#587168]">{chatNotice}</p>}
            </div>
          </div>
        )}

        <button
          type="button"
          aria-label="Open assistant chat"
          aria-haspopup="dialog"
          aria-expanded={chatOpen}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={finishDrag}
          style={{ touchAction: "none" }}
          className="group relative grid h-[62px] w-[62px] place-items-center rounded-full border border-[#8fc8bf] bg-[#bfe8e1] text-[#071a14] shadow-[0_18px_45px_rgba(11,16,14,0.28)] transition hover:bg-[#d1f0eb] focus:outline-none focus:ring-2 focus:ring-[#6baea4] focus:ring-offset-2"
          data-testid="assistant-launcher"
        >
          <span className="absolute inset-1 rounded-full border border-white/60" />
          <DragonflyMark size={29} />
          <Grip className="absolute -bottom-1 -left-1 rounded-full bg-[#f7f2e8] p-1 text-[#173d34]" size={17} />
        </button>

        <button
          type="button"
          onClick={openAccount}
          aria-label="Open assistant sign in"
          className="absolute -right-2 -top-3 grid h-8 w-8 place-items-center rounded-full border-2 border-[#f7f2e8] bg-[#071a14] text-[#bfe8e1] shadow-lg transition hover:scale-105 hover:bg-[#123329] focus:outline-none focus:ring-2 focus:ring-[#6baea4]"
          data-testid="assistant-account-bubble"
        >
          <UserRound size={14} strokeWidth={1.7} />
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] bg-[#020906]/80 p-0 backdrop-blur-md sm:p-5" data-testid="assistant-preview">
          <section role="dialog" aria-modal="true" aria-labelledby="assistant-title" className="relative mx-auto flex h-full max-w-[1500px] overflow-hidden bg-[#f7f2e8] text-[#071a14] shadow-2xl sm:rounded-[28px]">
            <button
              ref={closeRef}
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close assistant preview"
              className="absolute right-4 top-4 z-30 rounded-full border border-[#bfe8e1]/40 bg-[#071a14]/95 p-3 text-[#d5f1eb] transition hover:bg-[#123329] focus:outline-none focus:ring-2 focus:ring-[#8fc8bf] sm:right-6 sm:top-6"
            >
              <X size={19} />
            </button>

            {!entered ? (
              <div className="grid h-full w-full overflow-y-auto lg:grid-cols-[1.05fr_0.95fr]">
                <div className="relative flex min-h-[48vh] flex-col justify-between overflow-hidden bg-[#020906] p-7 text-[#f7f2e8] sm:p-10 lg:p-14">
                  <div className="absolute -right-28 -top-28 h-96 w-96 rounded-full border border-white/10" />
                  <div className="absolute -bottom-44 -left-16 h-[34rem] w-[34rem] rounded-full bg-[#bfe8e1]/10 blur-3xl" />
                  <div className="relative">
                    <div className="mb-10 flex items-center gap-3">
                      <div className="grid h-11 w-11 place-items-center rounded-full border border-[#bfe8e1]/35 bg-[#bfe8e1] text-[#071a14]"><DragonflyMark size={23} /></div>
                      <div>
                        <ReservedBrand />
                        <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#bfe8e1]/65">by Grace Under Umbrella</p>
                      </div>
                    </div>
                    <PreviewBadge />
                    <h2 id="assistant-title" className="mt-7 max-w-3xl font-display text-5xl font-light leading-[0.95] tracking-tight sm:text-6xl xl:text-7xl">
                      Intelligence that understands the whole operation.
                    </h2>
                    <p className="mt-6 max-w-xl text-base leading-7 text-[#d5f1eb]/65">
                      Explore three views of one governed agentic platform—owner intelligence, seller operations, and an enterprise-ready demonstration.
                    </p>
                  </div>
                  <div className="relative mt-10 grid gap-3 border-t border-[#bfe8e1]/20 pt-5 text-xs text-[#d5f1eb]/55 sm:grid-cols-3">
                    <span>One coherent intelligence layer</span>
                    <span>Governed capability routing</span>
                    <span>Permission-aware execution</span>
                  </div>
                </div>

                <div className="flex min-h-0 items-start justify-center overflow-y-auto p-6 pt-20 sm:p-10 sm:pt-20 lg:p-12 lg:pt-20">
                  {entryStep === "choose" ? (
                    <div className="w-full max-w-xl">
                      <p className="overline text-[#587168]">Choose your entrance</p>
                      <h3 className="mt-3 font-display text-4xl font-light">Sign in to your workspace.</h3>
                      <p className="mt-3 text-sm leading-6 text-[#61736b]">
                        Each role opens a different view. These sign-in screens are visual previews and are not connected to live accounts.
                      </p>
                      <div className="mt-6 grid gap-3">
                        {SIGN_IN_TYPES.map((type) => {
                          const Icon = type.icon;
                          return (
                            <button
                              key={type.id}
                              type="button"
                              onClick={() => chooseSignIn(type.id)}
                              className="group flex items-center gap-4 rounded-2xl border border-[#cec7b8] bg-white/75 p-4 text-left transition hover:-translate-y-0.5 hover:border-[#77b6ac] hover:bg-[#eef8f5] hover:shadow-md"
                            >
                              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#d5f1eb] text-[#0d3027]">
                                <Icon size={19} strokeWidth={1.5} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-medium text-[#102c24]">{type.label}</span>
                                <span className="mt-1 block text-xs leading-5 text-[#6a7b74]">{type.description}</span>
                              </span>
                              <ChevronRight size={18} className="shrink-0 text-[#789188] transition group-hover:translate-x-1" />
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          onClick={openGraceCustomerSignIn}
                          className="group flex items-center gap-4 rounded-2xl border border-[#cec7b8] bg-white/75 p-4 text-left transition hover:-translate-y-0.5 hover:border-[#77b6ac] hover:bg-[#eef8f5] hover:shadow-md"
                          data-testid="assistant-customer-signin-handoff"
                        >
                          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#d5f1eb] text-[#0d3027]">
                            <UserRound size={19} strokeWidth={1.5} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-medium text-[#102c24]">Grace Customer</span>
                            <span className="mt-1 block text-xs leading-5 text-[#6a7b74]">Continue to the website's existing customer sign-in.</span>
                          </span>
                          <ChevronRight size={18} className="shrink-0 text-[#789188] transition group-hover:translate-x-1" />
                        </button>
                      </div>
                      <div className="mt-6 border-t border-[#d7d0c2] pt-5 text-center">
                        <button type="button" onClick={enterGuestPreview} className="rounded-full bg-[#bfe8e1] px-5 py-3 text-xs font-medium uppercase tracking-[0.14em] text-[#0c3027] transition hover:bg-[#d5f1eb]">
                          Continue as guest
                        </button>
                        <p className="mt-3 text-[11px] text-[#708079]">Opens a locked Enterprise Customer demonstration.</p>
                      </div>
                    </div>
                  ) : (
                    <form className="w-full max-w-md" onSubmit={(event) => { event.preventDefault(); enterSignedPreview(); }}>
                      <button type="button" onClick={() => setEntryStep("choose")} className="text-xs text-[#587168] underline underline-offset-4">
                        ← Choose a different workspace
                      </button>
                      <div className="mt-6 flex items-center gap-4">
                        <span className="grid h-12 w-12 place-items-center rounded-full bg-[#d5f1eb] text-[#0d3027]">
                          <ActiveSignInIcon size={21} strokeWidth={1.5} />
                        </span>
                        <div>
                          <p className="overline text-[#587168]">Preview sign in</p>
                          <h3 className="mt-1 font-display text-3xl font-light">{activeSignIn.label}</h3>
                        </div>
                      </div>
                      <p className="mt-5 text-sm leading-6 text-[#61736b]">{activeSignIn.description}</p>
                      <label className="mt-7 block text-xs font-medium uppercase tracking-[0.16em] text-[#587168]" htmlFor={`${signInType}-email`}>Work email</label>
                      <input id={`${signInType}-email`} type="email" placeholder={activeSignIn.email} className="mt-2 w-full rounded-xl border border-[#cec7b8] bg-white px-4 py-3.5 text-sm outline-none focus:border-[#77b6ac]" />
                      <label className="mt-4 block text-xs font-medium uppercase tracking-[0.16em] text-[#587168]" htmlFor={`${signInType}-password`}>Password</label>
                      <input id={`${signInType}-password`} type="password" placeholder="Preview only" className="mt-2 w-full rounded-xl border border-[#cec7b8] bg-white px-4 py-3.5 text-sm outline-none focus:border-[#77b6ac]" />
                      {signInType === "enterprise" && (
                        <div className="mt-5 rounded-2xl border border-[#9fcfc8] bg-[#dff3ef] p-4">
                          <label className="block text-xs font-medium uppercase tracking-[0.16em] text-[#45675d]" htmlFor="enterprise-role">Organization role</label>
                          <select
                            id="enterprise-role"
                            value={enterpriseRole}
                            onChange={(event) => setEnterpriseRole(event.target.value)}
                            className="mt-2 w-full rounded-xl border border-[#9fcfc8] bg-white px-4 py-3 text-sm text-[#173d34] outline-none focus:border-[#5d9f95]"
                          >
                            <option value="owner">Owner</option>
                            <option value="manager">Manager</option>
                            <option value="employee">Employee</option>
                          </select>
                          <p className="mt-3 text-[11px] leading-5 text-[#587168]">
                            Enterprise navigation, records, permissions, and capabilities vary substantially according to the software package each organization purchases and the signed-in user's authorized role.
                          </p>
                        </div>
                      )}
                      <button type="submit" className="mt-6 flex w-full items-center justify-between rounded-xl bg-[#071a14] px-5 py-4 text-sm font-medium text-[#d5f1eb] transition hover:bg-[#123329]">
                        Enter {activeSignIn.label} preview <ChevronRight size={18} />
                      </button>
                    </form>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full flex-col overflow-hidden">
                <header className="flex flex-wrap items-center justify-between gap-4 border-b border-[#9fcfc8]/25 bg-[#020906] px-5 py-4 pr-20 text-[#f7f2e8] sm:px-8">
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-[#bfe8e1] text-[#071a14]"><DragonflyMark size={19} /></div>
                    <div>
                      <ReservedBrand compact />
                      <p className="mt-1 text-[9px] uppercase tracking-[0.2em] text-[#bfe8e1]/60">Grace Under Umbrella</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {guestMode && (
                      <span className="rounded-full bg-[#bfe8e1] px-3 py-1.5 text-[9px] font-medium uppercase tracking-[0.17em] text-[#071a14]">
                        Guest view · locked
                      </span>
                    )}
                    {!guestMode && modeId === "enterprise" && (
                      <span className="rounded-full border border-[#bfe8e1]/30 bg-[#bfe8e1]/10 px-3 py-1.5 text-[9px] font-medium uppercase tracking-[0.17em] text-[#bfe8e1]">
                        {enterpriseRole} role · package-configured
                      </span>
                    )}
                    <PreviewBadge />
                  </div>
                </header>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
                  <nav className="flex shrink-0 gap-2 overflow-x-auto border-b border-[#d5cec0] bg-[#f1ebdf] p-3 md:w-64 md:flex-col md:overflow-y-auto md:border-b-0 md:border-r md:p-4 xl:w-72 xl:p-5">
                    <p className="overline mb-2 hidden px-3 text-[#587168] md:block">Display mode</p>
                    {MODES.map((mode) => {
                      const Icon = mode.icon;
                      const selected = mode.id === modeId;
                      return (
                        <button
                          key={mode.id}
                          type="button"
                          disabled={guestMode}
                          onClick={() => { setModeId(mode.id); setActiveModule(null); }}
                          className={`flex min-w-max items-center gap-3 rounded-xl px-4 py-3 text-left text-sm transition md:w-full ${
                            selected
                              ? "bg-[#071a14] text-[#f7f2e8] shadow-lg"
                              : "text-[#3f5b52] hover:bg-white/75"
                          } ${guestMode ? "cursor-not-allowed opacity-55" : ""}`}
                        >
                          <Icon size={17} strokeWidth={1.5} />
                          <span><span className="block font-medium">{mode.label}</span><span className={`mt-0.5 hidden text-[10px] md:block ${selected ? "text-[#bfe8e1]/55" : "text-[#738078]"}`}>{mode.shortLabel} view</span></span>
                        </button>
                      );
                    })}
                    <div className="mt-auto hidden rounded-2xl border border-[#cfc7b8] bg-white/60 p-4 md:block">
                      <LockKeyhole size={17} className="text-[#173d34]" />
                      <p className="mt-3 text-xs font-medium">{guestMode ? "Guest screen locked" : "Preview boundary"}</p>
                      <p className="mt-1 text-[11px] leading-5 text-[#687a72]">
                        {guestMode
                          ? "Sign in through the small account bubble to explore navigable workspace previews."
                          : "Navigation is illustrative and does not execute actions or access live records."}
                      </p>
                    </div>
                  </nav>

                  <main className="relative min-h-0 flex-1 overflow-y-auto bg-[#f7f2e8] p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-6xl">
                      {guestMode && (
                        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[#9fcfc8] bg-[#dff3ef] p-4 text-[#173d34]">
                          <LockKeyhole className="mt-0.5 shrink-0" size={18} />
                          <div>
                            <p className="text-sm font-medium">Guest demonstration</p>
                            <p className="mt-1 text-xs leading-5 text-[#587168]">You can view the Enterprise experience, but its navigation and modules remain locked.</p>
                          </div>
                        </div>
                      )}

                      {activeModule && !guestMode ? (
                        <div>
                          <button type="button" onClick={() => setActiveModule(null)} className="text-xs font-medium text-[#44675d] underline underline-offset-4">
                            ← Back to {activeMode.label}
                          </button>
                          <div className="mt-7 flex flex-col gap-5 border-b border-[#d7d0c2] pb-8 sm:flex-row sm:items-start">
                            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#d5f1eb] text-[#0d3027]">
                              <ActiveModuleIcon size={24} strokeWidth={1.4} />
                            </span>
                            <div>
                              <p className="overline text-[#587168]">Navigable workspace preview</p>
                              <h3 className="mt-3 font-display text-4xl font-light sm:text-5xl">{activeModule[0]}</h3>
                              <p className="mt-4 max-w-2xl text-sm leading-6 text-[#61736b]">{activeModule[1]}</p>
                            </div>
                          </div>
                          <div className="mt-7 grid gap-4 md:grid-cols-3">
                            {[
                              ["Current signals", "A concise view of the records and conditions requiring attention."],
                              ["Recommended next step", "A governed recommendation would appear here before any consequential action."],
                              ["Evidence & history", "Supporting context, provenance, decisions, and rollback information stay attached."],
                            ].map(([title, description]) => (
                              <section key={title} className="rounded-2xl border border-[#d7d0c2] bg-white/80 p-5">
                                <p className="text-[10px] font-medium uppercase tracking-[0.17em] text-[#5e776e]">{title}</p>
                                <p className="mt-4 text-sm leading-6 text-[#52665f]">{description}</p>
                              </section>
                            ))}
                          </div>
                          <div className="mt-5 rounded-2xl bg-[#071a14] p-6 text-[#f7f2e8]">
                            <p className="font-display text-2xl">Preview boundary</p>
                            <p className="mt-2 max-w-3xl text-xs leading-6 text-[#bfe8e1]/65">
                              This page demonstrates navigation and information architecture only. It does not read records, execute tools, approve actions, or connect to private systems.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="overline text-[#587168]">{activeMode.eyebrow}</p>
                          <div className="mt-4 grid items-end gap-5 xl:grid-cols-[1fr_0.6fr]">
                            <h3 className="font-display text-4xl font-light leading-none sm:text-5xl">{activeMode.title}</h3>
                            <p className="text-sm leading-6 text-[#61736b]">{activeMode.description}</p>
                          </div>

                          <div className="mt-8 grid grid-cols-2 gap-3 xl:grid-cols-4">
                            {activeMode.stats.map(([label, value, note]) => (
                              <div key={label} className="rounded-2xl border border-[#d7d0c2] bg-white/85 p-4 sm:p-5">
                                <p className="text-[10px] uppercase tracking-[0.17em] text-[#65776f]">{label}</p>
                                <p className="mt-2 font-display text-3xl">{value}</p>
                                <p className="mt-1 text-[10px] text-[#718079]">{note}</p>
                              </div>
                            ))}
                          </div>

                          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {activeMode.sections.map(([title, description, Icon]) => (
                              <button
                                key={title}
                                type="button"
                                disabled={guestMode}
                                onClick={() => setActiveModule([title, description, Icon])}
                                className={`group rounded-2xl border border-[#d7d0c2] bg-white/90 p-5 text-left transition focus:outline-none focus:ring-2 focus:ring-[#6baea4] ${
                                  guestMode
                                    ? "cursor-not-allowed opacity-65"
                                    : "hover:-translate-y-0.5 hover:border-[#8fc8bf] hover:shadow-lg"
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <span className="grid h-10 w-10 place-items-center rounded-full bg-[#dff3ef] text-[#173d34]"><Icon size={18} strokeWidth={1.45} /></span>
                                  {guestMode ? <LockKeyhole size={16} className="text-[#789188]" /> : <ChevronRight size={17} className="text-[#789188] transition group-hover:translate-x-1" />}
                                </div>
                                <h4 className="mt-5 font-display text-2xl">{title}</h4>
                                <p className="mt-2 text-xs leading-5 text-[#65776f]">{description}</p>
                                <span className="mt-4 inline-block text-[9px] font-medium uppercase tracking-[0.17em] text-[#557268]">
                                  {guestMode ? "Sign in to explore" : "Open preview module"}
                                </span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </main>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
